import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';

import {
    VerificationService,
    SuccessResponse,
    closeTabRef,
    Country,
    Locale
} from '@sheerid/jslib';
import { getQueryParamsByName, sendPostMessage } from '../../lib/utils/browser';
import { SoftwareType } from '../../lib/types/types';
import { SoftwareTypesEnum, AccessLinksEnum, ProductCodeEnumCA } from '../../lib/types/runtimeTypes';
import { EDUCATOR_PORTAL_LINK, INTUIT_EDUCATION_URL, REPOSITION_MESSAGE } from '../../constants';


interface StepSuccessProps {
    verificationService: VerificationService;
    intl: InjectedIntl;
}

const StepSuccess = ({ verificationService, intl }: StepSuccessProps) => {
    // Close any open tab for SSO login
    closeTabRef();

    // the following is deemed as an unexpected use of a template literal, but it is expected for us
     // eslint-disable-next-line
    const rewardCode = (verificationService.verificationResponse as SuccessResponse).rewardCode || '${rewardCode}';
    const softwareType = getQueryParamsByName('type') as SoftwareType;
    const country = getQueryParamsByName('country') as Country;
    const locale = getQueryParamsByName('locale') || verificationService.verificationResponse.locale as Locale;

    const getAccessLinksFromSoftwareType = (softwareType: SoftwareType) => {
        let accessLink;

        if (softwareType === SoftwareTypesEnum.ONLINE) {
            accessLink = rewardCode;
        } else if (softwareType === SoftwareTypesEnum.TAX && locale === 'en-US' && country === 'CA') {
            accessLink = AccessLinksEnum.QUICKBOOKS_TAX_ACCESS_LINK_CA;
        } else if (softwareType === SoftwareTypesEnum.TAX && locale === 'en-US' && country === 'US') {
            accessLink = AccessLinksEnum.QUICKBOOKS_TAX_ACCESS_LINK;
        } else if (softwareType === SoftwareTypesEnum.TAX && locale === 'fr-CA') {
            accessLink = AccessLinksEnum.QUICKBOOKS_TAX_ACCESS_LINK_FR;
        }

        return accessLink;
    };

    React.useEffect(() => {
        // Send postMessage so that Intuit's site can reposition the scroll position of the user
        sendPostMessage(REPOSITION_MESSAGE, 'https://www.intuit.com');
    }, []);

    return (
        <div id="sid-step-success" className="sid-l-container">
            <div className="sid-header sid-l-space-top-md">
                {
                    (softwareType === SoftwareTypesEnum.TAX) ?
                        <>
                            { country === 'US' ?
                                <div className="sid-header__title sid-l-space-top-md">
                                    <FormattedHTMLMessage id={`step.success.${softwareType}.titleUS`} defaultMessage="Success! You've been verified." />
                                </div>
                                :
                                <div className="sid-header__title sid-l-space-top-md">
                                    <FormattedHTMLMessage id={`step.success.${softwareType}.title`} defaultMessage="Success! You've been verified." />
                                </div>
                            }
                        </>
                    :
                        <div className="sid-header__title sid-l-space-top-md">
                            <FormattedHTMLMessage id={`step.success.${softwareType}.title`} defaultMessage="Success! You've been verified." />
                        </div>
                }

                {
                    (softwareType === SoftwareTypesEnum.TAX) ?
                        <>
                            { (country === 'US') ?
                                <div className="sid-header__subtitle">
                                    <FormattedHTMLMessage
                                        id={`step.success.${softwareType}.subtitle`}
                                        defaultMessage="We have successfully verified your educator credentials. You will receive an email within 3 business days containing your product license information and download link."                            />
                                </div>
                                :
                                <div className="sid-header__subtitle">
                                <FormattedHTMLMessage
                                    id={`step.success.${softwareType}.subtitleCA`}
                                    defaultMessage="We have successfully verified your educator credentials. You will receive an email within 3 business days containing your product license information and download link."                            />
                                </div>
                            }
                        </>
                    :       
                        <div className="sid-header__subtitle">
                            <FormattedHTMLMessage
                                id={`step.success.${softwareType}.subtitle`}
                                defaultMessage="We have successfully verified your educator credentials. You will receive an email within 3 business days containing your product license information and download link."
                            />
                        </div>
                }

                {
                    (softwareType === SoftwareTypesEnum.TAX && country === 'CA') && (
                        <div className="sid-l-space-top-sm sid-tax-download-link">
                            <FormattedHTMLMessage
                                id="step.success.tax.downloadLinkInstructionsCA"
                                defaultMessage='You will receive your download instructions and license key within 3 business days.'
                            />
                        </div>
                    )
                }
            </div>

            {
                softwareType === SoftwareTypesEnum.ONLINE && (
                    <div className="sid-l-space-top-sm">
                            <div>
                                <div className="sid-l-space-top-sm">
                                    <FormattedHTMLMessage
                                        id="step.success.online.getStarted"
                                        defaultMessage="Steps to getting started:"
                                    />
                            </div>
                            <ol className="sid-intuit-ol-steps">
                                {
                                    country === 'CA' && locale === 'en-US' ?
                                        <li>
                                            <FormattedHTMLMessage
                                                id="step.success.online.createAccount"
                                                defaultMessage='<a class="sid-link" href="https://signup.quickbooks.intuit.com/?locale=en-ca&offerType=trial&bc=OBI-LL1&offerId=20001253&product=qboa&productRegion=EN-CA&sc=PAP-GEN3-QBOA-FIRM">Create your Quickbooks Online Accountant account</a>. When prompted to enter details about your "firm", use details about your educational institution.'
                                            />
                                        </li>
                                        :
                                        null
                                }
                                <li>
                                    <FormattedHTMLMessage
                                        id="step.success.online.onlineLogin"
                                        defaultMessage="<a class='sid-link' href='{educatorPortalLink}'>Login to the Intuit Educator Portal</a> using your new login credentials."
                                        values={{
                                            educatorPortalLink: EDUCATOR_PORTAL_LINK,
                                        }}
                                    />
                                </li>
                                <li>
                                    <FormattedHTMLMessage
                                        id="step.success.checkOut"
                                        defaultMessage="Check out the “How the Educator Portal Works” video on the Portal to learn how to set up your class(es), invite your students, and access resources."
                                    />
                                </li>
                            </ol>
                            </div>
                        </div>
                )
            }

            {
                (softwareType === SoftwareTypesEnum.TAX && country === 'US') && (
                    <div className="sid-l-space-top-sm">
                        <FormattedHTMLMessage
                            id="step.success.createAccount"
                            defaultMessage="<a class='sid-link' href='{accessLink}'>Create your account</a>"
                            values={{
                                accessLink: getAccessLinksFromSoftwareType(softwareType),
                            }}
                        />
                    </div>
                )
            }

            {
                ((softwareType === SoftwareTypesEnum.TAX) && (country === 'CA')) ?
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id={`step.success.${softwareType}.bestWishes`}
                            defaultMessage="If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>."
                        />
                    </div>
                    :
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id={`step.success.${softwareType}.bestWishesUS`}
                            defaultMessage="If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>."
                        />
                    </div>
            }

            {
                (country === 'US') ? (
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id="intuitEducationTeamUS"
                            defaultMessage="Thanks!<br>The Intuit Team"
                        />
                    </div>
                )
                :
                (
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id="intuitEducationTeam"
                            defaultMessage="Thanks!<br>The Intuit Team"
                        />
                    </div>
                )
            }

            <hr className="sid-success-rule sid-l-space-top-lg"></hr>

            <div className="sid-footer-logo">
                <a href={INTUIT_EDUCATION_URL}>
                    <img className="sid-footer-logo-img" src="https://assets-resources.sheerid.com/intuit/img/intuit-cornerstone-brand-lockup-2-line-color.png" alt="Intuit Education" />
                </a>
            </div>
        </div>
    );
};


export const StepSuccessComponent = injectIntl(StepSuccess);

